import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import moment from "moment";
import Tabs from "../Tabs";

export type PeriodSegment = "daily" | "weekly" | "monthly";

interface PeriodNavigatorProps {
  className?: string;
  activeTab: PeriodSegment;
  periodOffset: number;
  onTabChange: (tab: PeriodSegment) => void;
  onPrev: () => void;
  onNext: () => void;
  hideTabs?: boolean;
}

const PeriodNavigator: React.FC<PeriodNavigatorProps> = ({
  className,
  activeTab,
  periodOffset,
  onTabChange,
  onPrev,
  onNext,
  hideTabs = false,
}) => {
  const periodLabel = useMemo(() => {
    if (activeTab === "daily") {
      const d = moment().subtract(periodOffset, "day");
      return periodOffset === 0
        ? "Today"
        : periodOffset === 1
        ? "Yesterday"
        : d.format("D MMM");
    }
    if (activeTab === "weekly") {
      const start = moment().subtract(periodOffset, "week").startOf("week");
      const end = start.clone().endOf("week");
      return periodOffset === 0
        ? "This week"
        : `${start.format("D MMM")} – ${end.format("D MMM")}`;
    }
    const start = moment().subtract(periodOffset, "month").startOf("month");
    return periodOffset === 0 ? "This month" : start.format("MMM YYYY");
  }, [activeTab, periodOffset]);

  return (
    <div className={`flex items-center justify-between gap-2 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onPrev}
          className="bg-white rounded-full p-1.5 border border-gray-200"
          aria-label="Previous period"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-gray-600 font-medium min-w-[100px] text-center">
          {periodLabel}
        </span>
        <button
          type="button"
          onClick={onNext}
          disabled={periodOffset === 0}
          className="bg-white rounded-full p-1.5 border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Next period"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {!hideTabs && (
        <div className="w-1/3">
          <Tabs
            tabs={[
              { id: "daily", title: "D" },
              { id: "weekly", title: "W" },
              { id: "monthly", title: "M" },
            ]}
            activeTab={activeTab}
            onChange={(tab) => onTabChange(tab.id as PeriodSegment)}
          />
        </div>
      )}
    </div>
  );
};

export default PeriodNavigator;
